import React, { useState, useCallback, useMemo } from 'react';
import { closeModal } from 'utils/localDispatcher';
import { ChatOrderModalStyle } from './ChatOrderModalStyle';
import { createChatStoreOrder } from 'apis/CommerceGraphql/chatOrder';
import { openToast } from 'utils/localDispatcher';
import { ChatStoreProductInput, MoneyCurrency } from '__generated__/commerce';
import ChatOrderForm from './form/ChatOrderForm';
import { CloseIcon } from 'utils/SVGIcons';

interface ChatOrderModalProps {
  buyerAccountId: string;
  storeId: string;
  buyerName?: string;
}

const PRODUCT_CHIPS_STORAGE_KEY = 'chatOrder_productChips';
const MAX_PRODUCT_CHIPS = 5;

const getStoredProductChips = (): string[] => {
  try {
    const stored = localStorage.getItem(PRODUCT_CHIPS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return [];
};

const saveProductChip = (productName: string): void => {
  const trimmed = productName.trim();
  if (!trimmed) return;

  const current = getStoredProductChips();
  const filtered = current.filter((chip) => chip !== trimmed);
  const updated = [trimmed, ...filtered].slice(0, MAX_PRODUCT_CHIPS);

  localStorage.setItem(PRODUCT_CHIPS_STORAGE_KEY, JSON.stringify(updated));
};

const ChatOrderModal = ({ buyerAccountId, storeId, buyerName }: ChatOrderModalProps) => {
  const [productName, setProductName] = useState('');
  const [price, setPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const productChips = useMemo(() => getStoredProductChips(), []);

  const handleProductNameChange = useCallback((value: string) => {
    setProductName(value);
  }, []);

  const handlePriceChange = useCallback((value: string) => {
    setPrice(value);
  }, []);

  const validateForm = useCallback(() => {
    if (!productName.trim()) {
      alert('상품명을 입력해주세요.');
      return false;
    }
    if (!price || parseInt(price, 10) <= 0) {
      alert('금액을 입력해주세요.');
      return false;
    }
    return true;
  }, [productName, price]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const productInputs: ChatStoreProductInput[] = [
        {
          productName: productName.trim(),
          price: {
            amount: parseInt(price, 10),
            currency: MoneyCurrency.Won,
          },
          quantity: 1,
        },
      ];

      const result = await createChatStoreOrder({
        buyerAccountId,
        storeId,
        products: productInputs,
        entry: 'chat_header',
      });

      if (result.error) {
        switch (result.error.__typename) {
          case 'ChatOrderInvalidOrder':
            setIsSubmitting(false);
            alert('결제 요청에 실패했어요. 입력을 다시 확인해주세요.');
            return;

          case 'ChatOrderStoreNotFound':
            setIsSubmitting(false);
            alert('결제 요청에 실패했어요. 가게를 찾을 수 없어요.');
            return;

          case 'ChatOrderStoreNotOwned':
            setIsSubmitting(false);
            alert('결제 요청에 실패했어요. 가게 소유권이 없어요.');
            return;
        }
        setIsSubmitting(false);
        alert('결제 요청에 실패했어요. 잠시 후 다시 시도해주세요.');
        return;
      }

      saveProductChip(productName);
      openToast('결제 요청이 전송되었어요.');
      closeModal();
    } catch (err) {
      alert('결제 요청에 실패했어요. 잠시 후 다시 시도해주세요.');
      setIsSubmitting(false);
    }
  };

  return (
    <ChatOrderModalStyle>
      <button type="button" className="close-button" onClick={closeModal}>
        <CloseIcon size={24} />
      </button>

      <div className="title">결제요청</div>

      {buyerName && (
        <>
          <div className="customer-section">
            <div className="customer-label">이용 고객</div>
            <div className="customer-name">{buyerName}</div>
          </div>
          <div className="customer-divider" />
        </>
      )}

      <ChatOrderForm
        productName={productName}
        price={price}
        isSubmitting={isSubmitting}
        productChips={productChips}
        onProductNameChange={handleProductNameChange}
        onPriceChange={handlePriceChange}
        onSubmit={handleSubmit}
      />
    </ChatOrderModalStyle>
  );
};

export default ChatOrderModal;
